// Socle de tokens sémantiques `--s-*` — la source unique du thème, partagée
// entre l'extension et la console.
//
// POURQUOI ICI. Les styles de `previewCss` (modale d'aperçu, panneau de
// réglages, popover de retouche) sont écrits contre ces variables, et ils sont
// rendus dans les deux apps. Tant que chaque app déclarait sa propre copie du
// socle, deux pannes silencieuses étaient possibles :
//
//   - une valeur modifiée d'un seul côté → les deux apps divergent
//     progressivement, sans que rien ne casse ni n'avertisse ;
//   - un token absent d'un côté → la règle CSS retombe sur sa valeur initiale
//     (fond transparent, couleur héritée) et le composant partagé s'affiche à
//     moitié habillé, toujours sans erreur.
//
// Les deux ont eu lieu. Le socle est donc défini une fois, ici, et les apps ne
// choisissent plus que l'endroit où le poser.
//
// Exporté comme CHAÎNE, comme `previewCss` et pour la même raison : l'extension
// l'injecte dans un shadow DOM (pas de feuille de style de la page hôte à
// laquelle se raccrocher), la console dans une balise <style>.
//
// AUCUNE dépendance plateforme, conformément à la règle du paquet.

/**
 * Thème clair. Valeurs de référence : ce sont celles que voit l'utilisateur.
 *
 * NOTE SUR L'ACCENT — deux tokens, pas un. `--s-accent` sert au TEXTE, aux
 * bordures et aux icônes ; `--s-accent-fill` sert aux APLATS sous du texte
 * `--s-on-accent`. En clair ils ont la même valeur, ce qui donne envie de n'en
 * garder qu'un : ne pas le faire. En sombre ils divergent nécessairement, un
 * bleu lisible EN texte sur fond sombre étant trop clair pour porter du texte
 * blanc DESSUS. La confusion des deux rôles est exactement ce qui donnait un
 * bouton principal à 2,9:1 dans le noir, sans que rien ne le signale.
 */
const LIGHT = `
  --s-surface: #ffffff; --s-surface-2: #f3f4f6; --s-surface-3: #fafbfc;
  --s-skeleton: #e5e9ef; --s-track: #e6eaee;
  /* '--s-ink-3' était gray-500 (#6b7280) : 4,83:1 sur du blanc, mais 4,39:1
     sur '--s-surface-2' — or c'est précisément là que vivent les textes d'aide
     (panneaux, barre latérale, tuiles). Assombri d'un cran pour tenir 4,5:1
     sur les DEUX surfaces, pas seulement sur la plus favorable. */
  --s-ink: #1d2226; --s-ink-2: #4b5563; --s-ink-3: #646b77;
  --s-line: #e5e7eb; --s-line-strong: #d1d5db; --s-line-2: #eef1f5;
  --s-accent: #0a66c2; --s-accent-hover: #004182; --s-accent-tint: #eaf2fb;
  --s-accent-border: #bfdbfe; --s-accent-disabled: #b0c4de;
  --s-accent-fill: #0a66c2; --s-accent-fill-hover: #004182; --s-on-accent: #ffffff;
  --s-indigo: #6366f1; --s-indigo-strong: #4338ca; --s-indigo-soft: #c7d2fe;
  --s-good: #1a7f37; --s-good-strong: #166534; --s-good-tint: #eaf7ef; --s-good-soft: #a7ddba;
  --s-warn: #b74700; --s-warn-fill: #d97706; --s-warn-tint: #fff8ec; --s-warn-soft: #f4d9a4;
  --s-danger: #cf222e; --s-danger-tint: #fdecec; --s-danger-soft: #eaa8a8;
`

/** Thème sombre. Strictement additif : il n'existe aucun token qui ne soit
 *  d'abord défini en clair (le test du paquet le vérifie). */
const DARK = `
  --s-surface: #1b2532; --s-surface-2: #131b26; --s-surface-3: #1f2a38;
  --s-skeleton: #2a3646; --s-track: #2c3a4d;
  --s-ink: #e8ecf3; --s-ink-2: #aeb9c8; --s-ink-3: #8b96a6;
  --s-line: #2c3a4d; --s-line-strong: #3a4a60; --s-line-2: #22303f;
  --s-accent: #4a9be8; --s-accent-hover: #6fb2f0; --s-accent-tint: #14273f;
  --s-accent-border: #295080; --s-accent-disabled: #33455f;
  /* En sombre, l'accent de TEXTE et l'accent d'APLAT ne peuvent pas être la
     même couleur — voir le commentaire du bloc clair. #4a9be8 est éclairci
     pour rester lisible en texte (6,4:1 sur la page) ; sous du blanc, il ne
     donne que 2,9:1. L'aplat est donc plus foncé, et son survol l'assombrit
     encore au lieu de l'éclaircir, sinon le texte blanc repasserait sous le
     seuil au moment précis où l'on interagit. */
  --s-accent-fill: #1f6feb; --s-accent-fill-hover: #1a5cc8; --s-on-accent: #ffffff;
  --s-indigo: #8b8df6; --s-indigo-strong: #a5a7f8; --s-indigo-soft: #2b2e57;
  --s-good: #4ade80; --s-good-strong: #86efac; --s-good-tint: #12291c; --s-good-soft: #1f4030;
  --s-warn: #fbbf24; --s-warn-fill: #f59e0b; --s-warn-tint: #2a2110; --s-warn-soft: #4a3a18;
  --s-danger: #f87171; --s-danger-tint: #2c1616; --s-danger-soft: #522a2a;
`

/** Les déclarations brutes, sans sélecteur — pour les tests et les outils. */
export const themeTokens = { light: LIGHT, dark: DARK }

export interface ThemeCssOptions {
  /** Où poser le thème clair. `:host` dans un shadow DOM, `:root` sinon. */
  selector: string
  /**
   * Sélecteur du sombre automatique (`prefers-color-scheme: dark`).
   * Par défaut `selector` : l'app suit l'OS, sans échappatoire.
   * Une app qui offre un réglage de thème doit exclure le clair forcé, par ex.
   * `:root:not([data-theme="light"])`, sinon le média l'emporterait dessus.
   */
  autoDarkSelector?: string
  /**
   * Sélecteur du sombre forcé, pour une app qui laisse choisir le thème
   * (`:root[data-theme="dark"]`). Omis : pas de bascule manuelle.
   */
  forcedDarkSelector?: string
}

/**
 * Rend le socle sous forme de feuille de style, posé aux endroits demandés.
 * Une seule définition des valeurs, quel que soit le nombre de sélecteurs.
 */
export function themeCss({ selector, autoDarkSelector, forcedDarkSelector }: ThemeCssOptions): string {
  const blocs = [
    `${selector} {${LIGHT}}`,
    `@media (prefers-color-scheme: dark) {\n  ${autoDarkSelector ?? selector} {${DARK}}\n}`,
  ]
  if (forcedDarkSelector) blocs.push(`${forcedDarkSelector} {${DARK}}`)
  return blocs.join('\n')
}
